import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add, Edit, Delete, HtmlOutlined } from '@mui/icons-material';
import { useGetHtmlComponentsQuery } from '@/state/queries/html-components/use-get-html-components';
import { useGetHtmlPlaceholdersQuery } from '@/state/queries/html-components/use-get-html-placeholders';
import { useCreateHtmlComponentMutation } from '@/state/mutations/html-components/use-create-html-component';
import { useUpdateHtmlComponentMutation } from '@/state/mutations/html-components/use-update-html-component';
import { useDeleteHtmlComponentMutation } from '@/state/mutations/html-components/use-delete-html-component';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import type { HtmlComponentDto } from '@/state/queries/html-components/use-get-html-components';

export default function HtmlComponentsPage() {
  const { data: components, isLoading, error } = useGetHtmlComponentsQuery();
  const { data: placeholders } = useGetHtmlPlaceholdersQuery();
  const createMutation = useCreateHtmlComponentMutation();
  const updateMutation = useUpdateHtmlComponentMutation();
  const deleteMutation = useDeleteHtmlComponentMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<HtmlComponentDto | null>(null);
  const [name, setName] = useState('');
  const [html, setHtml] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setHtml('');
    setEditing(null);
    setSaveError(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleOpenEdit = (c: HtmlComponentDto) => {
    setEditing(c);
    setName(c.name);
    setHtml(c.html);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, data: { name, html } });
      } else {
        await createMutation.mutateAsync({ name, html });
      }
      setModalOpen(false);
      resetForm();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save HTML component');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this HTML component?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <HtmlOutlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    HTML Components
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate} sx={{ flexShrink: 0 }}>
                  Create
                </Button>
              </Box>
            }
          />

          <Card sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CardContent
              sx={{
                'flex': 1,
                'minHeight': 0,
                'display': 'flex',
                'flexDirection': 'column',
                'overflow': 'hidden',
                'p': 0,
                '&:last-child': { pb: 0 },
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography>Loading...</Typography>
                </Box>
              ) : error ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="error">Error loading components.</Typography>
                </Box>
              ) : !components || components.length === 0 ? (
                <Box sx={{ p: 4 }}>
                  <EmptyState
                    text="No HTML components yet"
                    icon={<HtmlOutlined sx={{ fontSize: 48, color: 'text.secondary' }} />}
                  />
                </Box>
              ) : (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {components.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {c.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{c.version}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton size="small" onClick={() => handleOpenEdit(c)} aria-label="Edit">
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(c.id)}
                                aria-label="Delete"
                                color="error"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {placeholders && Object.keys(placeholders).length > 0 && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Placeholder reference (use {'{{path}}'} in HTML)
                </Typography>
                <Box
                  component="pre"
                  sx={{ fontSize: 12, overflow: 'auto', maxHeight: 120, p: 1, bgcolor: 'action.hover' }}
                >
                  {Object.entries(placeholders).map(([path, def]) => (
                    <Typography key={path} component="span" variant="caption" sx={{ display: 'block' }}>
                      {path}: {def.description}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>{editing ? 'Edit HTML Component' : 'Create HTML Component'}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {saveError && (
                  <Alert severity="error" onClose={() => setSaveError(null)}>
                    {saveError}
                  </Alert>
                )}
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  disabled={!!editing}
                />
                <TextField
                  label="HTML"
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  fullWidth
                  multiline
                  rows={12}
                  placeholder={'Use {{path}} for placeholders'}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!name || !html || createMutation.isPending || updateMutation.isPending}
              >
                {editing ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      }
    />
  );
}
